/**
 * Booking overlay — take a booking, hand the vehicle over, take it back.
 *
 * The three tabs follow the life of a loan rather than the shape of the table:
 * you book it, you hand it over (licence sighted, condition recorded, agreement
 * signed), and later you take it back and settle up.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { addDays, format } from 'date-fns';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  IdCard,
  Loader2,
  Search,
  Trash2,
  UserRound,
  X
} from 'lucide-react';

import { dataApi } from '../../services/api';
import { getFilePreview, BUCKETS } from '../../services/appwrite';
import {
  EMPTY_CONDITION,
  fleetBookingCreate,
  fleetBookingDelete,
  fleetBookingHandOver,
  fleetBookingReturn,
  fleetBookingUpdate,
  fleetDeleteFile,
  fleetUploadDataUrl,
  fleetUploadFile,
  parseCondition,
  serialiseCondition
} from '../../services/fleet';
import {
  BODY_TYPES,
  BOOKING_TYPES,
  VEHICLE_SIZES,
  bookingVehicleId,
  chargeableDays,
  estimateCharge,
  findAvailableVehicles,
  findConflicts,
  formatCurrency,
  relatedId,
  vehicleDescription,
  vehicleLabel
} from '../../lib/fleet';
import { useObfuscate } from '../../hooks/useObfuscate';
import { cn } from '../../lib/utils';

import ConditionDiagram from '../../components/fleet/ConditionDiagram';
import SignaturePad from '../../components/fleet/SignaturePad';
import LinkCustomerDialog from './LinkCustomerDialog';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import DeleteConfirmDialog from '../../components/dialogs/DeleteConfirmDialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CurrencyInput } from '../../components/ui/currency-input';
import { DatePicker, DateTimePicker } from '../../components/ui/date-picker';
import { PersonLookup } from '../../components/ui/person-lookup';
import { Combobox } from '../../components/ui/combobox';
import { NumberInput } from '../../components/ui/number-input';
import RecordHistoryButton from '../../components/history/RecordHistoryButton';

const toLocalInput = (iso) => (iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : '');
const numberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const FUEL_STEPS = [
  { value: 0, label: 'E' },
  { value: 25, label: '¼' },
  { value: 50, label: '½' },
  { value: 75, label: '¾' },
  { value: 100, label: 'F' }
];

const customerName = (customer) =>
  customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email || 'Customer' : '';

const FleetBookingForm = ({
  booking,
  isOpen,
  onClose,
  onSaved,
  vehicles = [],
  bookings = [],
  defaults = {},
  now = new Date()
}) => {
  const priv = useObfuscate();
  const isEdit = Boolean(booking?.$id);

  const [form, setForm] = useState({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [conditionOut, setConditionOut] = useState(EMPTY_CONDITION);
  const [conditionIn, setConditionIn] = useState(EMPTY_CONDITION);
  const [signature, setSignature] = useState(null);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('booking');
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [showFinder, setShowFinder] = useState(false);
  const [requirements, setRequirements] = useState({});

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // ---------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------

  // The reset below must fire when the dialog opens, and only then. Vehicles
  // arrive over realtime and change identity whenever anyone edits one, which
  // would otherwise wipe out whatever is half-typed in this form.
  const openContextRef = useRef({ vehicles, defaults, booking });
  openContextRef.current = { vehicles, defaults, booking };

  const bookingKey = booking?.$id || null;

  useEffect(() => {
    if (!isOpen) return;

    const { vehicles: vehicleList = [], defaults: rawDefaults, booking: rawBooking } = openContextRef.current;
    // A new booking arrives as null, which a destructuring default would not catch.
    const source = rawBooking || {};
    const openDefaults = rawDefaults || {};
    const start = source.scheduledStart || openDefaults.scheduledStart || new Date();
    const end = source.scheduledEnd || openDefaults.scheduledEnd || addDays(new Date(start), 3);
    const vehicleId = bookingVehicleId(source) || openDefaults.vehicleId || null;
    const vehicle = vehicleList.find((v) => v.$id === vehicleId);

    setForm({
      type: source.type || openDefaults.type || 'loan',
      status: source.status || 'reserved',
      vehicle: vehicleId,
      title: source.title || '',
      scheduledStart: toLocalInput(start),
      scheduledEnd: source.$id && !source.scheduledEnd ? '' : toLocalInput(end),
      // Only an existing booking can already be open-ended; a new one starts
      // with the three-day default above.
      indefinite: Boolean(source.$id && !source.scheduledEnd),
      notes: source.notes || '',
      quoteRequest: relatedId(source.quoteRequest) || openDefaults.quoteRequestId || null,
      dailyRate: source.dailyRate ?? vehicle?.dailyRate ?? '',
      adminFee: source.adminFee ?? vehicle?.adminFee ?? '',
      chargeToCustomer: source.chargeToCustomer !== false,
      allocatedToUserId: source.allocatedToUserId || null,
      driverName: source.driverName || '',
      driverPhone: source.driverPhone || '',
      driverEmail: source.driverEmail || '',
      licenceNumber: source.licenceNumber || '',
      licenceVersion: source.licenceVersion || '',
      licenceExpiry: source.licenceExpiry ? String(source.licenceExpiry).split('T')[0] : '',
      licenceClass: source.licenceClass || '',
      licenceSighted: Boolean(source.licenceSightedAt),
      licenceImageId: source.licenceImageId || null,
      agreementSignedName: source.agreementSignedName || '',
      agreementSignatureId: source.agreementSignatureId || null,
      odometerOut: source.odometerOut ?? '',
      fuelLevelOut: source.fuelLevelOut ?? 100,
      odometerIn: source.odometerIn ?? '',
      fuelLevelIn: source.fuelLevelIn ?? 100,
      newDamageReported: Boolean(source.newDamageReported),
      damageDescription: ''
    });

    setConditionOut(parseCondition(source.conditionOut));
    setConditionIn(parseCondition(source.conditionIn));
    setSignature(null);
    setCustomer(typeof source.customer === 'object' ? source.customer : null);
    setRequirements({});
    setShowFinder(!vehicleId);
    setTab(source.status === 'out' ? 'return' : 'booking');
  }, [isOpen, bookingKey]);

  useEffect(() => {
    if (!isOpen) return;
    dataApi
      .quoteRequestList({})
      .then((rows) => setQuoteRequests(rows.filter((r) => r.needsLoanVehicle)))
      .catch(() => setQuoteRequests([]));
    dataApi
      .userList()
      .then(setOrgUsers)
      .catch(() => setOrgUsers([]));
  }, [isOpen]);

  // A customer stored only as an id needs fetching so we can show their name.
  useEffect(() => {
    const id = relatedId(booking?.customer);
    if (!isOpen || !id || customer?.$id === id) return;
    dataApi
      .customerGet(id)
      .then(setCustomer)
      .catch(() => setCustomer(null));
  }, [isOpen, booking, customer?.$id]);

  // ---------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.$id === form.vehicle) || null,
    [vehicles, form.vehicle]
  );

  const isLoan = form.type === 'loan';
  const isAllocation = form.type === 'allocation';
  // Allocations go through the same handover as a loan — a staff car still
  // leaves with an odometer reading and comes back with one.
  const hasHandover = isLoan || isAllocation;

  const candidate = useMemo(
    () => ({
      $id: booking?.$id,
      vehicle: form.vehicle,
      type: form.type,
      status: form.status,
      scheduledStart: form.scheduledStart ? new Date(form.scheduledStart).toISOString() : null,
      scheduledEnd: form.indefinite || !form.scheduledEnd ? null : new Date(form.scheduledEnd).toISOString(),
      actualStart: booking?.actualStart || null,
      actualEnd: booking?.actualEnd || null
    }),
    [booking, form.vehicle, form.type, form.status, form.scheduledStart, form.scheduledEnd, form.indefinite]
  );

  const conflicts = useMemo(
    () => (form.vehicle ? findConflicts(candidate, bookings, now) : []),
    [candidate, bookings, now, form.vehicle]
  );

  const charge = useMemo(
    () =>
      estimateCharge(
        {
          chargeToCustomer: form.chargeToCustomer,
          dailyRate: numberOrNull(form.dailyRate),
          adminFee: numberOrNull(form.adminFee),
          scheduledStart: form.scheduledStart,
          scheduledEnd: form.indefinite ? null : form.scheduledEnd,
          actualStart: booking?.actualStart,
          actualEnd: booking?.actualEnd,
          status: form.status
        },
        now
      ),
    [
      form.chargeToCustomer,
      form.dailyRate,
      form.adminFee,
      form.scheduledStart,
      form.scheduledEnd,
      form.indefinite,
      form.status,
      booking,
      now
    ]
  );

  const availableVehicles = useMemo(() => {
    if (!form.scheduledStart) return vehicles;
    return findAvailableVehicles(vehicles, bookings, {
      start: new Date(form.scheduledStart),
      // Undefined end means open-ended: free from the pickup onwards.
      end: form.indefinite || !form.scheduledEnd ? undefined : new Date(form.scheduledEnd),
      requirements,
      excludeBookingId: booking?.$id,
      now
    });
  }, [vehicles, bookings, form.scheduledStart, form.scheduledEnd, form.indefinite, requirements, booking, now]);

  const dayCount = chargeableDays(form.scheduledStart, form.scheduledEnd);

  const quoteRequestOptions = useMemo(
    () =>
      quoteRequests.map((qr) => ({
        value: qr.$id,
        label: `QR-${String(qr.$sequence || 0).padStart(5, '0')}`,
        description: [qr.customer ? priv.name(customerName(qr.customer)) : null, qr.vehicle?.registration]
          .filter(Boolean)
          .join(' · ')
      })),
    [quoteRequests, priv]
  );

  // ---------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------

  const buildPayload = () => ({
    type: form.type,
    vehicle: form.vehicle || null,
    customer: customer?.$id || null,
    quoteRequest: form.quoteRequest || null,
    title: form.title || null,
    scheduledStart: new Date(form.scheduledStart).toISOString(),
    scheduledEnd: form.indefinite ? null : new Date(form.scheduledEnd).toISOString(),
    notes: form.notes || null,
    dailyRate: numberOrNull(form.dailyRate),
    adminFee: numberOrNull(form.adminFee),
    chargeToCustomer: Boolean(form.chargeToCustomer),
    allocatedToUserId: form.allocatedToUserId || null,
    driverName: form.driverName || null,
    driverPhone: form.driverPhone || null,
    driverEmail: form.driverEmail || null,
    licenceNumber: form.licenceNumber || null,
    licenceVersion: form.licenceVersion || null,
    licenceExpiry: form.licenceExpiry ? new Date(form.licenceExpiry).toISOString() : null,
    licenceClass: form.licenceClass || null,
    licenceImageId: form.licenceImageId || null
  });

  const validate = () => {
    if (!form.scheduledStart) {
      toast.error('A pickup date is required');
      return false;
    }
    if (!form.indefinite && !form.scheduledEnd) {
      toast.error('Set a return date, or turn on “No fixed return date”');
      return false;
    }
    if (!form.indefinite && new Date(form.scheduledEnd) <= new Date(form.scheduledStart)) {
      toast.error('The return must be after the pickup');
      return false;
    }
    // A loan or allocation may sit unassigned in the waiting list; a service
    // booking is meaningless without the vehicle it is for.
    if (!form.vehicle && form.type === 'maintenance') {
      toast.error('Pick a vehicle');
      return false;
    }
    if (isAllocation && !form.driverName.trim()) {
      toast.error('Say who the vehicle is allocated to');
      return false;
    }
    if (conflicts.length > 0) {
      toast.error('That vehicle is already booked over those dates');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = isEdit ? await fleetBookingUpdate(booking.$id, payload) : await fleetBookingCreate(payload);
      toast.success(isEdit ? 'Booking updated' : 'Booking created');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      console.error('Failed to save booking:', err);
      toast.error(err?.message || 'Could not save this booking');
    } finally {
      setSaving(false);
    }
  };

  const handleHandOver = async () => {
    if (!form.vehicle) {
      toast.error('Assign a vehicle before handing it over');
      setTab('booking');
      return;
    }
    // Sighting a licence is a customer-loan obligation; staff allocations are
    // covered by employment records, so it is not forced there.
    if (isLoan && !form.licenceSighted) {
      toast.error('Confirm the driver’s licence has been sighted first');
      return;
    }
    if (!form.driverName.trim()) {
      toast.error('Record who is taking the vehicle');
      return;
    }

    setSaving(true);
    try {
      // Save the booking first so the handover writes onto current details.
      const current = isEdit ? await fleetBookingUpdate(booking.$id, buildPayload()) : await fleetBookingCreate(buildPayload());

      let signatureId = form.agreementSignatureId;
      if (signature) {
        signatureId = await fleetUploadDataUrl(signature, `agreement-${current.$id}.png`);
      }

      const saved = await fleetBookingHandOver(
        { ...current, vehicle: selectedVehicle || current.vehicle },
        {
          odometerOut: numberOrNull(form.odometerOut),
          fuelLevelOut: numberOrNull(form.fuelLevelOut),
          conditionOut: serialiseCondition(conditionOut),
          extra: {
            licenceSightedAt: form.licenceSighted ? new Date().toISOString() : null,
            agreementSignedName: form.agreementSignedName || form.driverName || null,
            agreementSignedAt: signatureId ? new Date().toISOString() : null,
            agreementSignatureId: signatureId || null
          }
        }
      );

      toast.success('Vehicle handed over');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      console.error('Handover failed:', err);
      toast.error(err?.message || 'Could not complete the handover');
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async () => {
    setSaving(true);
    try {
      const saved = await fleetBookingReturn(
        { ...booking, vehicle: selectedVehicle || booking.vehicle },
        {
          odometerIn: numberOrNull(form.odometerIn),
          fuelLevelIn: numberOrNull(form.fuelLevelIn),
          conditionIn: serialiseCondition(conditionIn),
          newDamageReported: Boolean(form.newDamageReported),
          damageDescription: form.damageDescription || null,
          totalCharged: charge.total
        }
      );
      toast.success('Vehicle returned');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      console.error('Return failed:', err);
      toast.error(err?.message || 'Could not record the return');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      const saved = await fleetBookingUpdate(booking.$id, { status: 'cancelled' });
      toast.success('Booking cancelled');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not cancel this booking');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEdit) return;
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await fleetBookingDelete(booking);
      toast.success('Booking deleted');
      onSaved?.({ ...booking, _deleted: true });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not delete this booking');
    }
  };

  const uploadPhotos = useCallback(async (files, which) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const ids = await Promise.all(Array.from(files).map((file) => fleetUploadFile(file)));
      const apply = (prev) => ({ ...prev, photos: [...(prev.photos || []), ...ids] });
      if (which === 'out') setConditionOut(apply);
      else setConditionIn(apply);
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Could not upload those photos');
    } finally {
      setUploading(false);
    }
  }, []);

  const removePhoto = async (fileId, which) => {
    const apply = (prev) => ({ ...prev, photos: (prev.photos || []).filter((id) => id !== fileId) });
    if (which === 'out') setConditionOut(apply);
    else setConditionIn(apply);
    await fleetDeleteFile(fileId);
  };

  // ---------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------

  const photoStrip = (condition, which, readOnly) => (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(condition.photos || []).map((fileId) => (
          <div key={fileId} className="group relative">
            <img
              src={getFilePreview(BUCKETS.FLEET_DOCUMENTS, fileId, 160, 160)}
              alt="Vehicle condition"
              className="h-20 w-20 rounded-md border object-cover"
            />
            {!readOnly && (
              <button
                type="button"
                onClick={() => removePhoto(fileId, which)}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="Remove photo"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {!readOnly && (
          <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            <span className="text-[10px]">Add</span>
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
              onChange={(e) => {
                uploadPhotos(e.target.files, which);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </div>
    </div>
  );

  const fuelSelector = (value, onChange, disabled) => (
    <div className="flex gap-1">
      {FUEL_STEPS.map((step) => (
        <button
          key={step.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(step.value)}
          className={cn(
            'flex-1 rounded border py-1.5 text-xs font-medium transition-colors',
            Number(value) === step.value
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border hover:bg-accent'
          )}
        >
          {step.label}
        </button>
      ))}
    </div>
  );

  const canHandOver = isEdit ? booking.status === 'reserved' : true;
  const canReturn = isEdit && booking.status === 'out';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[92vh] w-260 max-w-[96vw] flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>{isEdit ? 'Booking' : 'New booking'}</DialogTitle>
              {isEdit && <RecordHistoryButton rowId={booking.$id} title="Booking" />}
            </div>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="w-fit">
              <TabsTrigger value="booking">Booking</TabsTrigger>
              {hasHandover && <TabsTrigger value="handover">Handover</TabsTrigger>}
              {hasHandover && <TabsTrigger value="return">Return</TabsTrigger>}
              {isLoan && <TabsTrigger value="charges">Charges</TabsTrigger>}
            </TabsList>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {/* ---------------------------------------------- Booking */}
              <TabsContent value="booking" className="space-y-4">
                {conflicts.length > 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-medium">This vehicle is already booked</p>
                      <ul className="mt-1 space-y-0.5 text-xs">
                        {conflicts.map((c) => (
                          <li key={c.$id}>
                            {format(new Date(c.scheduledStart), 'd MMM')} →{' '}
                            {c.scheduledEnd ? format(new Date(c.scheduledEnd), 'd MMM') : 'ongoing'}
                            {typeof c.customer === 'object' && c.customer ? ` · ${priv.name(customerName(c.customer))}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">When</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Select value={form.type} onValueChange={(v) => set('type', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BOOKING_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Pickup</Label>
                      <DateTimePicker
                        value={form.scheduledStart || ''}
                        onChange={(v) => set('scheduledStart', v)}
                        placeholder="Pick a date"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Due back</Label>
                      {form.indefinite ? (
                        <div className="flex h-10 items-center rounded-md border border-dashed px-3 text-sm text-muted-foreground">
                          No end date
                        </div>
                      ) : (
                        <DateTimePicker
                          value={form.scheduledEnd || ''}
                          onChange={(v) => set('scheduledEnd', v)}
                          placeholder="Pick a date"
                        />
                      )}
                      {isLoan && !form.indefinite && (
                        <p className="text-[11px] text-muted-foreground">
                          {dayCount} chargeable day{dayCount === 1 ? '' : 's'}
                        </p>
                      )}
                    </div>

                    {/* Runs until someone ends it — the usual shape of a staff
                        allocation, and occasionally of a long repair. */}
                    <div className="flex items-center justify-between rounded-md border p-3 sm:col-span-3">
                      <div>
                        <Label className="text-sm">No fixed return date</Label>
                        <p className="text-[11px] text-muted-foreground">
                          The vehicle stays out until the booking is ended
                        </p>
                      </div>
                      <Switch
                        checked={Boolean(form.indefinite)}
                        onCheckedChange={(v) => {
                          setForm((prev) => ({
                            ...prev,
                            indefinite: v,
                            // Restore a sensible window when a date is wanted again.
                            scheduledEnd: v
                              ? ''
                              : prev.scheduledEnd ||
                                toLocalInput(addDays(new Date(prev.scheduledStart || Date.now()), 3))
                          }));
                        }}
                      />
                    </div>

                    {form.type === 'maintenance' && (
                      <div className="space-y-1.5 sm:col-span-3">
                        <Label className="text-xs text-muted-foreground">Reason</Label>
                        <Input
                          value={form.title || ''}
                          onChange={(e) => set('title', e.target.value)}
                          placeholder="New tyres"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex-row items-center justify-between pb-3">
                    <CardTitle className="text-sm">Vehicle</CardTitle>
                    <Button size="sm" variant="ghost" onClick={() => setShowFinder((v) => !v)}>
                      <Search className="mr-1 h-3.5 w-3.5" />
                      {showFinder ? 'Hide' : 'Find'}
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedVehicle ? (
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="font-medium">{vehicleLabel(selectedVehicle)}</p>
                          <p className="text-xs text-muted-foreground">
                            {priv.rego(selectedVehicle.registration)} · {vehicleDescription(selectedVehicle)}
                            {selectedVehicle.transmission ? ` · ${selectedVehicle.transmission}` : ''}
                            {selectedVehicle.seats ? ` · ${selectedVehicle.seats} seats` : ''}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => set('vehicle', null)}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                        No vehicle assigned — this booking sits in the waiting list until one is picked.
                      </p>
                    )}

                    {showFinder && (
                      <div className="space-y-3 rounded-md border p-3">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          <Select
                            value={requirements.bodyType || 'any'}
                            onValueChange={(v) => setRequirements((p) => ({ ...p, bodyType: v === 'any' ? undefined : v }))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Any type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any type</SelectItem>
                              {BODY_TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <Select
                            value={requirements.vehicleSize || 'any'}
                            onValueChange={(v) => setRequirements((p) => ({ ...p, vehicleSize: v === 'any' ? undefined : v }))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Any size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any size</SelectItem>
                              {VEHICLE_SIZES.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <NumberInput
                            placeholder="Min seats"
                            max={20}
                            value={requirements.minSeats ?? null}
                            onChange={(v) => setRequirements((p) => ({ ...p, minSeats: v ?? undefined }))}
                          />
                        </div>

                        <div className="max-h-56 space-y-1.5 overflow-y-auto">
                          {availableVehicles.length === 0 ? (
                            <p className="py-4 text-center text-sm text-muted-foreground">
                              Nothing free that matches. Widen the requirements or shift the dates.
                            </p>
                          ) : (
                            availableVehicles.map((v) => (
                              <button
                                key={v.$id}
                                type="button"
                                onClick={() => {
                                  set('vehicle', v.$id);
                                  if (form.dailyRate === '' || form.dailyRate === null) set('dailyRate', v.dailyRate ?? '');
                                  if (form.adminFee === '' || form.adminFee === null) set('adminFee', v.adminFee ?? '');
                                  setShowFinder(false);
                                }}
                                className={cn(
                                  'flex w-full items-center justify-between rounded-md border p-2 text-left transition-colors hover:bg-accent',
                                  form.vehicle === v.$id && 'border-primary bg-accent'
                                )}
                              >
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium">{vehicleLabel(v)}</p>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {priv.rego(v.registration)}
                                    {v.transmission ? ` · ${v.transmission}` : ''}
                                    {v.seats ? ` · ${v.seats} seats` : ''}
                                    {v.fuelType ? ` · ${v.fuelType}` : ''}
                                  </p>
                                </div>
                                {v.dailyRate ? (
                                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                    {formatCurrency(v.dailyRate)}/day
                                  </span>
                                ) : null}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {isLoan && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Customer</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {customer ? (
                        <div className="flex items-center justify-between rounded-md border p-3">
                          <div className="flex items-center gap-2">
                            <UserRound className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{priv.name(customerName(customer))}</p>
                              <p className="text-xs text-muted-foreground">
                                {priv.phone(customer.phone)} {customer.email ? `· ${priv.email(customer.email, customer.firstName)}` : ''}
                              </p>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => setCustomer(null)}>
                            Change
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setCustomerPickerOpen(true)}>
                          <UserRound className="mr-1 h-4 w-4" /> Link a customer
                        </Button>
                      )}

                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Related quote request</Label>
                        <Combobox
                          value={form.quoteRequest}
                          onChange={(v) => set('quoteRequest', v)}
                          options={quoteRequestOptions}
                          placeholder="Search by number, customer or rego…"
                          emptyText="No quote requests are waiting on a loan vehicle"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Mirrors the Customer card above — an allocation has a person
                    on it just as a loan does. */}
                {isAllocation && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Allocated to</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {form.driverName ? (
                        <div className="flex items-center justify-between rounded-md border p-3">
                          <div className="flex items-center gap-2">
                            <UserRound className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{priv.name(form.driverName)}</p>
                              <p className="text-xs text-muted-foreground">
                                {form.allocatedToUserId ? 'Staff member' : 'Named person'}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setForm((prev) => ({ ...prev, allocatedToUserId: null, driverName: '' }))}
                          >
                            Change
                          </Button>
                        </div>
                      ) : (
                        <>
                          <PersonLookup
                            users={orgUsers}
                            value={{ userId: form.allocatedToUserId, name: form.driverName }}
                            onChange={({ userId, name }) =>
                              setForm((prev) => ({ ...prev, allocatedToUserId: userId, driverName: name }))
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Pick a staff member, or type any name for someone without a login.
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ---------------------------------------------- Handover */}
              <TabsContent value="handover" className="space-y-4">
                {booking?.handedOverAt && (
                  <div className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                    <CheckCircle2 className="h-4 w-4" />
                    Handed over {format(new Date(booking.handedOverAt), 'd MMM yyyy, h:mma')}
                  </div>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <IdCard className="h-4 w-4" /> {isAllocation ? 'Driver' : 'Driver & licence'}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {isAllocation
                        ? 'Who is taking the vehicle, and how to reach them.'
                        : 'The licence must be sighted before the vehicle leaves.'}
                    </p>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Driver name</Label>
                      {isAllocation ? (
                        <PersonLookup
                          users={orgUsers}
                          value={{ userId: form.allocatedToUserId, name: form.driverName }}
                          onChange={({ userId, name }) =>
                            setForm((prev) => ({ ...prev, allocatedToUserId: userId, driverName: name }))
                          }
                        />
                      ) : (
                        <Input
                          value={form.driverName || ''}
                          onChange={(e) => set('driverName', e.target.value)}
                          placeholder={customer ? customerName(customer) : ''}
                        />
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <Input value={form.driverPhone || ''} onChange={(e) => set('driverPhone', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <Input type="email" value={form.driverEmail || ''} onChange={(e) => set('driverEmail', e.target.value)} />
                    </div>
                    {!isAllocation && (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Licence number</Label>
                          <Input
                            value={form.licenceNumber || ''}
                            onChange={(e) => set('licenceNumber', e.target.value.toUpperCase())}
                            className="font-mono uppercase"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Version</Label>
                          <Input
                            value={form.licenceVersion || ''}
                            onChange={(e) => set('licenceVersion', e.target.value.replace(/\D/g, '').slice(0, 3))}
                            placeholder="123"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Expiry</Label>
                          <DatePicker
                            value={form.licenceExpiry || ''}
                            onChange={(v) => set('licenceExpiry', v)}
                            placeholder="Licence expiry"
                          />
                        </div>

                        {form.licenceExpiry && new Date(form.licenceExpiry) < now && (
                          <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 sm:col-span-3 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            This licence has expired — do not hand the vehicle over.
                          </div>
                        )}

                        <div className="flex items-center gap-2 rounded-md border p-3 sm:col-span-3">
                          <Checkbox
                            id="licenceSighted"
                            checked={Boolean(form.licenceSighted)}
                            onCheckedChange={(v) => set('licenceSighted', v)}
                          />
                          <Label htmlFor="licenceSighted" className="text-sm">
                            I have sighted the driver’s licence
                          </Label>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Condition going out</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Odometer (km)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.odometerOut ?? ''}
                          onChange={(e) => set('odometerOut', e.target.value)}
                          placeholder={selectedVehicle?.odometer ? String(selectedVehicle.odometer) : ''}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Fuel / charge</Label>
                        {fuelSelector(form.fuelLevelOut, (v) => set('fuelLevelOut', v), Boolean(booking?.handedOverAt))}
                      </div>
                    </div>

                    <ConditionDiagram
                      zones={conditionOut.zones}
                      onChange={(zones) => setConditionOut((prev) => ({ ...prev, zones }))}
                      readOnly={Boolean(booking?.handedOverAt)}
                    />

                    {photoStrip(conditionOut, 'out', Boolean(booking?.handedOverAt))}

                    <Textarea
                      rows={2}
                      placeholder="Anything else worth noting"
                      value={conditionOut.notes || ''}
                      onChange={(e) => setConditionOut((prev) => ({ ...prev, notes: e.target.value }))}
                      disabled={Boolean(booking?.handedOverAt)}
                    />
                  </CardContent>
                </Card>

                {/* Staff allocations are covered by employment terms, so only a
                    customer loan needs an agreement signed. */}
                {isLoan && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="h-4 w-4" /> Loan agreement
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Signed by</Label>
                      <Input
                        value={form.agreementSignedName || ''}
                        onChange={(e) => set('agreementSignedName', e.target.value)}
                        placeholder={form.driverName || 'Full name'}
                      />
                    </div>
                    <SignaturePad
                      value={
                        booking?.agreementSignatureId
                          ? getFilePreview(BUCKETS.FLEET_DOCUMENTS, booking.agreementSignatureId, 600, 200)
                          : null
                      }
                      onChange={setSignature}
                      disabled={Boolean(booking?.agreementSignedAt)}
                    />
                    {booking?.agreementSignedAt && (
                      <p className="text-xs text-muted-foreground">
                        Signed {format(new Date(booking.agreementSignedAt), 'd MMM yyyy, h:mma')}
                      </p>
                    )}
                  </CardContent>
                </Card>
                )}
              </TabsContent>

              {/* ---------------------------------------------- Return */}
              <TabsContent value="return" className="space-y-4">
                {!canReturn && !booking?.returnedAt && (
                  <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                    The vehicle has not gone out yet.
                  </p>
                )}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Condition coming back</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Odometer (km)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.odometerIn ?? ''}
                          onChange={(e) => set('odometerIn', e.target.value)}
                          disabled={!canReturn}
                        />
                        {form.odometerIn && form.odometerOut && (
                          <p className="text-[11px] text-muted-foreground">
                            {Math.max(0, Number(form.odometerIn) - Number(form.odometerOut)).toLocaleString()} km travelled
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Fuel / charge</Label>
                        {fuelSelector(form.fuelLevelIn, (v) => set('fuelLevelIn', v), !canReturn)}
                        {Number(form.fuelLevelIn) < Number(form.fuelLevelOut) && (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400">
                            Returned with less fuel than it went out with.
                          </p>
                        )}
                      </div>
                    </div>

                    <ConditionDiagram
                      zones={conditionIn.zones}
                      onChange={(zones) => setConditionIn((prev) => ({ ...prev, zones }))}
                      readOnly={!canReturn}
                    />

                    {photoStrip(conditionIn, 'in', !canReturn)}

                    <div className="flex items-center gap-2 rounded-md border p-3">
                      <Checkbox
                        id="newDamage"
                        checked={Boolean(form.newDamageReported)}
                        onCheckedChange={(v) => set('newDamageReported', v)}
                        disabled={!canReturn}
                      />
                      <Label htmlFor="newDamage" className="text-sm">
                        New damage since handover
                      </Label>
                    </div>

                    {form.newDamageReported && (
                      <Textarea
                        rows={2}
                        placeholder="What is the new damage?"
                        value={form.damageDescription || ''}
                        onChange={(e) => set('damageDescription', e.target.value)}
                        disabled={!canReturn}
                      />
                    )}

                    <Textarea
                      rows={2}
                      placeholder="Return notes"
                      value={conditionIn.notes || ''}
                      onChange={(e) => setConditionIn((prev) => ({ ...prev, notes: e.target.value }))}
                      disabled={!canReturn}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ---------------------------------------------- Charges */}
              <TabsContent value="charges" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Charges</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <Label className="text-sm">Charge the customer</Label>
                        <p className="text-[11px] text-muted-foreground">Turn off for a free courtesy car</p>
                      </div>
                      <Switch
                        checked={Boolean(form.chargeToCustomer)}
                        onCheckedChange={(v) => set('chargeToCustomer', v)}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Day rate</Label>
                        <CurrencyInput
                          value={numberOrNull(form.dailyRate) ?? 0}
                          onChange={(v) => set('dailyRate', v)}
                          disabled={!form.chargeToCustomer}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Admin fee</Label>
                        <CurrencyInput
                          value={numberOrNull(form.adminFee) ?? 0}
                          onChange={(v) => set('adminFee', v)}
                          disabled={!form.chargeToCustomer}
                        />
                      </div>
                    </div>

                    <div className="rounded-md border">
                      <div className="flex justify-between border-b px-3 py-2 text-sm">
                        <span className="text-muted-foreground">
                          {charge.days} day{charge.days === 1 ? '' : 's'} × {formatCurrency(charge.dailyRate)}
                        </span>
                        <span className="tabular-nums">{formatCurrency(charge.dailyTotal)}</span>
                      </div>
                      <div className="flex justify-between border-b px-3 py-2 text-sm">
                        <span className="text-muted-foreground">Admin fee</span>
                        <span className="tabular-nums">{formatCurrency(charge.adminFee)}</span>
                      </div>
                      <div className="flex justify-between px-3 py-2 font-semibold">
                        <span>{charge.ongoing ? 'Total so far' : 'Total'}</span>
                        <span className="tabular-nums">{formatCurrency(charge.total)}</span>
                      </div>
                    </div>

                    {charge.ongoing ? (
                      <p className="text-xs text-muted-foreground">
                        No return date set, so this is charged to today and keeps climbing until the
                        booking is ended.
                      </p>
                    ) : (
                      booking?.status === 'out' && (
                        <p className="text-xs text-muted-foreground">
                          Recalculated from the actual hire once the vehicle comes back.
                        </p>
                      )
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="flex-row items-center justify-between gap-2 border-t pt-4 sm:justify-between">
            <div className="flex gap-1">
              {isEdit && booking.status !== 'cancelled' && booking.status !== 'returned' && (
                <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
                  Cancel booking
                </Button>
              )}
              {isEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Save is always available; the primary action is whatever step
                the booking is actually at. */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>

              <Button
                variant={hasHandover && (canHandOver || canReturn) ? 'secondary' : 'default'}
                onClick={handleSave}
                disabled={saving || conflicts.length > 0}
              >
                {saving ? 'Saving…' : isEdit ? 'Save' : 'Create booking'}
              </Button>

              {hasHandover && canReturn && (
                <Button onClick={handleReturn} disabled={saving}>
                  Record return
                </Button>
              )}

              {hasHandover && canHandOver && (
                <Button onClick={handleHandOver} disabled={saving || conflicts.length > 0}>
                  Hand over
                </Button>
              )}
            </div>
          </DialogFooter>
          <DeleteConfirmDialog
            open={confirmDeleteOpen}
            onOpenChange={setConfirmDeleteOpen}
            onConfirm={confirmDelete}
          />
        </DialogContent>
      </Dialog>

      <LinkCustomerDialog
        isOpen={customerPickerOpen}
        individualOnly
        title="Who is taking the vehicle?"
        onClose={() => setCustomerPickerOpen(false)}
        onLinked={(entity) => {
          setCustomer(entity);
          setForm((prev) => ({
            ...prev,
            driverName: prev.driverName || customerName(entity),
            driverPhone: prev.driverPhone || entity.phone || '',
            driverEmail: prev.driverEmail || entity.email || ''
          }));
          setCustomerPickerOpen(false);
        }}
      />
    </>
  );
};

export default FleetBookingForm;
